import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../../components/common/Modal';
import { usersService, UserAction, UserDetail } from '../../services/api/users';
import { useToast } from '../../contexts/ToastContext';

interface UserActionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  user: UserDetail | null;
}

const UserActionsModal: React.FC<UserActionsModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  user,
}) => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState<string | null>(null);
  const [tempPassword, setTempPassword] = useState('');

  const handleAction = async (action: UserAction) => {
    if (!user) return;

    if (action === UserAction.RESET_TEMP_PASSWORD && (!tempPassword || tempPassword.length < 8)) {
      showError('Password must be at least 8 characters');
      return;
    }

    setLoading(action);
    try {
      const data = {
        action,
        tempPassword: action === UserAction.RESET_TEMP_PASSWORD ? tempPassword : undefined,
      };
      const result = await usersService.performAction(user.id, data);
      showSuccess(result.message || `Action ${action} completed successfully`);
      setTempPassword('');
      onSuccess();
      
      // Close modal after lock/unlock actions
      if (action === UserAction.LOCK || action === UserAction.UNLOCK) {
        onClose();
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Action failed';
      showError(errorMessage);
    } finally {
      setLoading(null);
    }
  };

  if (!user) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${t('common.actions')}: ${user.email}`}
      size="medium"
    >
      <div>
        {/* Lock/Unlock Section */}
        <div className={`action-section ${user.isActive ? 'lock' : 'unlock'}`}>
          {user.isActive ? (
            <>
              <h4>🔒 {t('users.actions.lock')}</h4>
              <p>Locking this account will prevent the user from logging in.</p>
              <button
                className="button button-secondary"
                onClick={() => handleAction(UserAction.LOCK)}
                disabled={loading !== null}
              >
                {loading === UserAction.LOCK ? t('common.loading') : t('users.actions.lock')}
              </button>
            </>
          ) : (
            <>
              <h4>🔓 {t('users.actions.unlock')}</h4>
              <p>Unlocking this account will allow the user to log in again.</p>
              <button
                className="button button-primary"
                onClick={() => handleAction(UserAction.UNLOCK)}
                disabled={loading !== null}
              >
                {loading === UserAction.UNLOCK ? t('common.loading') : t('users.actions.unlock')}
              </button>
            </>
          )}
        </div>

        {/* Send Reset Link Section */}
        <div className="action-section reset">
          <h4>📧 Send Password Reset Link</h4>
          <p>Send an email to the user with a link to reset their password.</p>
          <button
            className="button button-secondary"
            onClick={() => handleAction(UserAction.SEND_RESET_LINK)}
            disabled={loading !== null}
          >
            {loading === UserAction.SEND_RESET_LINK ? t('common.loading') : 'Send Reset Link'}
          </button>
        </div>

        {/* Set Temporary Password Section */}
        <div className="action-section reset">
          <h4>🔑 {t('users.actions.resetPassword')}</h4>
          <p>Set a temporary password for the user. They should change it on first login.</p>
          <div className="password-input-group">
            <input
              type="text"
              placeholder="Enter temporary password (min 8 characters)"
              value={tempPassword}
              onChange={(e) => setTempPassword(e.target.value)}
            />
          </div>
          <button
            className="button button-secondary"
            onClick={() => handleAction(UserAction.RESET_TEMP_PASSWORD)}
            disabled={loading !== null || !tempPassword || tempPassword.length < 8}
          >
            {loading === UserAction.RESET_TEMP_PASSWORD ? t('common.loading') : 'Set Temporary Password'}
          </button>
        </div>
      </div>

      <div className="form-actions">
        <button type="button" className="button button-secondary" onClick={onClose}>
          {t('common.close')}
        </button>
      </div>
    </Modal>
  );
};

export default UserActionsModal;
